/**
 * CONSAI Business App - Bank Details Component
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Landmark, Loader2, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankDetails = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder: '',
    branch: '',
    upi_id: ''
  });

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/bank-details`, { withCredentials: true });
      if (res.data.bank_details) {
        setFormData(res.data.bank_details);
      }
    } catch (err) {
      console.error('Failed to load bank details');
    }
    setLoading(false);
  };

  useEffect(() => { fetchBankDetails(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/bank-details`, formData, { withCredentials: true });
      toast.success(language === 'hi' ? 'बैंक विवरण सहेजा गया' : 'Bank details saved');
    } catch (err) {
      toast.error('Failed to save bank details');
    }
    setSaving(false);
  };

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-manrope">
          <Landmark className="h-5 w-5 text-[#021945]" />
          {language === 'hi' ? 'बैंक विवरण' : 'Bank Details'}
          <span className="text-sm font-normal text-slate-400">(Optional)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <p className="text-sm text-slate-500">
              {language === 'hi' ? 'ये विवरण आपके इनवॉइस PDF में दिखाई देंगे' : 'These details will appear in your invoice PDFs'}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs text-slate-500">{language === 'hi' ? 'बैंक का नाम' : 'Bank Name'}</label>
                <Input 
                  placeholder="HDFC Bank, SBI, ICICI..." 
                  value={formData.bank_name || ''}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  data-testid="bank-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">{language === 'hi' ? 'खाता संख्या' : 'Account Number'}</label>
                <Input 
                  placeholder="XXXXXXXXXXXX" 
                  value={formData.account_number || ''}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  data-testid="account-number-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">IFSC Code</label>
                <Input 
                  placeholder="HDFCXXXXXXX" 
                  value={formData.ifsc_code || ''}
                  onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })}
                  data-testid="ifsc-code-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">{language === 'hi' ? 'खाताधारक का नाम' : 'Account Holder'}</label>
                <Input 
                  placeholder="Name as in bank account" 
                  value={formData.account_holder || ''}
                  onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                  data-testid="account-holder-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">{language === 'hi' ? 'शाखा' : 'Branch'}</label>
                <Input 
                  placeholder="Branch name" 
                  value={formData.branch || ''}
                  onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">UPI ID (Optional)</label>
                <Input 
                  placeholder="yourname@upi" 
                  value={formData.upi_id || ''}
                  onChange={(e) => setFormData({ ...formData, upi_id: e.target.value })}
                  data-testid="upi-id-input"
                />
              </div>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#021945] hover:bg-[#021945]/90"
              data-testid="save-bank-details-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {language === 'hi' ? 'सहेजें' : 'Save Bank Details'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BankDetails;
